import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageShell } from "../components/Layout";
import KundaliChart from "../components/KundaliChart";
import CelebrityAvatar from "../components/CelebrityAvatar";
import CelebrityAskAIWidget from "../components/CelebrityAskAIWidget";
import PaywallGate from "../components/PaywallGate";
import SEO, { SITE_URL } from "../components/SEO";
import api from "../lib/api";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { Crown, Sparkle, ArrowLeft, Calendar, MapPin, Clock, Info, ShareNetwork, Check, CaretRight, CaretDown, ClockCounterClockwise, LockKey } from "@phosphor-icons/react";

// Stable, deterministic birth-details stub used for the lifetime per-user
// celebrity-data unlock. Every user hits the same fingerprint, so the unlock
// is effectively account-wide — one payment unlocks the whole catalogue.
const CELEB_LIFETIME_BIRTH_DETAILS = {
  date: "2000-01-01",
  time: "00:00",
  place: "global",
  timezone: "UTC",
};

// Three free-preview celebrities (one per category) so users can see the full
// depth of the analysis BEFORE paying. The remaining 48 detail pages keep
// hero + D1 + story free, with D9 + Dasha analysis gated.
const PREVIEW_SLUGS = new Set(["jobs-steve", "eminem-marshall", "castro-edwin", "osho-rajneesh", "elizabeth-ii"]);

const PLANET_COLOR = {
  Sun: "#F59E0B", Moon: "#94A3B8", Mars: "#EF4444", Mercury: "#10B981",
  Jupiter: "#F59E0B", Venus: "#EC4899", Saturn: "#3B82F6", Rahu: "#8B5CF6", Ketu: "#A855F7",
};

const RATING_STYLE = {
  AA: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/40", desc: "From birth certificate — the highest Rodden rating. Birth time is verified to the minute." },
  A:  { bg: "bg-sky-500/15",     text: "text-sky-300",     border: "border-sky-500/40",     desc: "From the person's own statement or close family. Reliable, but not certificate-verified." },
  B:  { bg: "bg-amber-500/15",   text: "text-amber-300",   border: "border-amber-500/40",   desc: "From a biography or autobiography. Treat house placements with mild caution." },
  X:  { bg: "bg-zinc-500/15",    text: "text-zinc-300",    border: "border-zinc-500/40",    desc: "Birth time unknown — chart cast for noon. Sign placements remain accurate; house placements are approximate." },
};

function renderInline(text) {
  // Render **bold** segments without dangerouslySetInnerHTML.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="text-gold font-semibold">{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

export default function CelebrityDetailPage() {
  const { slug, category } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [shareState, setShareState] = useState("idle"); // 'idle' | 'copied' | 'shared'

  useEffect(() => {
    setData(null);
    setError(null);
    api.get(`/celebrities/detail/${slug}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err?.response?.status === 401 ? "__auth__" : (err?.response?.data?.detail || "Failed to load celebrity chart.")));
  }, [slug]);

  // Members-only database (Oct 2026 anti-scrape policy): anonymous visitors
  // get a sign-in wall instead of chart data — no exceptions.
  if (error === "__auth__") {
    return (
      <PageShell>
        <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="celebrity-signin-wall">
          <div className="rounded-2xl bg-white shadow-xl border border-amber-300/60 p-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-800 mb-3">
              <LockKey size={22} weight="duotone" />
            </div>
            <h2 className="font-serif-display text-2xl font-light text-[#3F2D1B]">Members-only chart</h2>
            <p className="text-sm text-muted mt-2 font-light leading-relaxed">
              Our celebrity database is proprietary research. Sign in free to view this
              chart, the D9 Navamsa, and the rise-Dasha breakdown.
            </p>
            <button
              type="button"
              onClick={() => startGoogleSignIn(window.location.href)}
              className="btn-primary rounded-full w-full mt-5 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2"
              data-testid="celebrity-signin-btn"
            >
              <Sparkle size={14} weight="fill" /> Sign in free to view
            </button>
            <Link to="/celebrities" className="block text-xs text-muted mt-3 underline underline-offset-2" data-testid="celebrity-wall-back">
              ← Back to all celebrities
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <p className="text-rose-400 mb-4">{error}</p>
          <Link to="/celebrities" className="btn-primary rounded-full px-6 py-2.5 inline-block" data-testid="celebrity-back-link">← Back to all celebrities</Link>
        </div>
      </PageShell>
    );
  }

  if (!data) {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center text-muted">Computing chart…</div>
      </PageShell>
    );
  }

  const c = data.celebrity;
  // Prefer URL category for the back link; fall back to celebrity's own category.
  const cat = category || c.category || "billionaires";
  const catLabel =
    cat === "rags-to-riches" ? "Rags to Riches" :
    cat === "lottery-winners" ? "Lottery Winners" :
    cat === "spiritual-gurus" ? "Spiritual Gurus" :
    cat === "royals" ? "Kings & Queens" :
    cat === "internet-famous" ? "Sudden Fame · Internet" :
    "Billionaires";
  const rd = data.rise_dasha;
  const rs = RATING_STYLE[c.rodden_rating] || RATING_STYLE.X;
  const mdColor = PLANET_COLOR[rd.md_lord] || "#FFB800";
  const adColor = PLANET_COLOR[rd.ad_lord] || "#FFB800";

  const shareUrl = `${SITE_URL}/celebrities/${cat}/${c.slug}`;
  const ogImage  = `${SITE_URL}/api/celebrities/og/${c.slug}.png`;
  const dashaLabel = rd.sd_lord
    ? `${rd.md_lord} → ${rd.ad_lord} → ${rd.pd_lord} → ${rd.sd_lord}`
    : `${rd.md_lord} → ${rd.ad_lord}`;
  const shareText = `${c.name}'s birth chart — ${dashaLabel} period catapulted them. See the math on AstroKuber:`;

  const isPreview = PREVIEW_SLUGS.has(c.slug);

  const handleShare = async () => {
    const payload = { title: `${c.name} · AstroKuber Celebrity Data`, text: shareText, url: shareUrl };
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share(payload);
        setShareState("shared");
        setTimeout(() => setShareState("idle"), 2000);
        return;
      } catch {
        /* user cancelled — fall through to copy */
      }
    }
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setShareState("copied");
        setTimeout(() => setShareState("idle"), 2000);
      } catch {
        /* clipboard blocked — no-op */
      }
    }
  };

  return (
    <PageShell>
      <SEO
        title={`${c.name} Birth Chart — D1, D9 + The Vimshottari Dasha They Rose Under`}
        description={`Verified birth chart of ${c.name} — D1 (Rasi) + D9 (Navamsa) + the exact Vimshottari Mahadasha-Antardasha they were running when ${data.rise_event.label}. Verified historical record · Rodden ${c.rodden_rating}. Free, instant, no signup.`}
        path={`/celebrities/${cat}/${c.slug}`}
        image={ogImage}
        keywords={[
          `${c.name} birth chart`, `${c.name} kundali`, `${c.name} horoscope`, `${c.name} astrology`,
          `${c.name} navamsa`, `${c.name} d9 chart`, `${c.name} dasha`, `${c.name} vimshottari`,
          `${c.name} mahadasha`, `${c.name} rodden rating`, `${c.name} verified birth chart`,
          "billionaire birth chart", "celebrity kundali", "what dasha made them rich",
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          "name": c.name,
          "nationality": c.country,
          "jobTitle": "Billionaire entrepreneur",
          "worksFor": { "@type": "Organization", "name": c.company },
          "birthDate": c.dob,
          "birthPlace": { "@type": "Place", "name": c.place },
          "url": `https://astrokuberchat.com/celebrities/${c.slug}`,
        }}
      />

      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-8 pb-24">
        <div className="flex items-center justify-between mb-6">
          <Link to={`/celebrities/${cat}`} className="inline-flex items-center gap-2 text-muted hover:text-gold text-sm" data-testid="back-to-celebrities">
            <ArrowLeft size={14} /> All {catLabel.toLowerCase()}
          </Link>
          <button
            onClick={handleShare}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFB800]/12 hover:bg-[#FFB800]/22 border border-[#FFB800]/40 text-gold text-sm font-medium transition-all"
            data-testid="share-chart-btn"
            aria-label="Share this celebrity chart"
          >
            {shareState === "copied" ? (
              <><Check size={14} weight="bold" /> Link copied</>
            ) : shareState === "shared" ? (
              <><Check size={14} weight="bold" /> Shared</>
            ) : (
              <><ShareNetwork size={14} weight="fill" /> Share this chart</>
            )}
          </button>
        </div>

        {/* Hero */}
        <section className="grid lg:grid-cols-[160px_1fr] gap-6 items-start mb-10" data-testid="celebrity-hero">
          {c.photo ? (
            <img
              src={c.photo}
              alt={c.name}
              loading="lazy"
              className="w-[160px] h-[200px] object-cover object-[center_25%] rounded-2xl border border-soft shadow-md"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <CelebrityAvatar name={c.name} size={160} className="rounded-2xl border border-soft" />
          )}
          <div>
            <div className="flex items-center gap-2 label-eyebrow text-gold">
              <Crown size={14} weight="fill" /> {c.industry}
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 font-light">{c.name}</h1>
            <div className="text-muted text-base mt-1">{c.company} · {c.country}</div>

            <div className="flex flex-wrap gap-3 mt-5 text-xs">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-bg/40 border border-soft text-muted">
                <Calendar size={12} /> {c.dob}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-bg/40 border border-soft text-muted">
                <Clock size={12} /> {c.time} ({c.tz})
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-bg/40 border border-soft text-muted">
                <MapPin size={12} /> {c.place}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${rs.bg} ${rs.text} ${rs.border}`} data-testid="rodden-rating-badge">
                Rodden {c.rodden_rating}
              </span>
            </div>

            <div className="mt-4 text-xs text-muted normal-case tracking-normal flex items-start gap-2 max-w-2xl">
              <Info size={12} className="mt-0.5 flex-shrink-0" />
              <span><strong className="text-foreground">Source:</strong> {c.source}. {rs.desc}</span>
            </div>
            {c.notes && (
              <div className="mt-3 text-xs text-muted normal-case tracking-normal max-w-2xl">
                <strong className="text-foreground">Note:</strong> {c.notes}
              </div>
            )}
            {c.rags_to_riches_story && (
              <div className="mt-5 p-4 rounded-xl bg-gradient-to-br from-[#8B5CF6]/12 to-[#EC4899]/8 border border-[#8B5CF6]/30 max-w-3xl" data-testid="rags-to-riches-story">
                <div className="text-[10px] uppercase tracking-widest text-purple-300 mb-2">Rags to Riches</div>
                <p className="text-sm leading-relaxed text-foreground/90">{c.rags_to_riches_story}</p>
              </div>
            )}
            {c.lottery_story && (
              <div className="mt-5 p-4 rounded-xl bg-gradient-to-br from-[#10B981]/12 to-[#06B6D4]/8 border border-[#10B981]/30 max-w-3xl" data-testid="lottery-story">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2">The Winning Story</div>
                <p className="text-sm leading-relaxed text-foreground/90">{c.lottery_story}</p>
              </div>
            )}
            {c.guru_story && (
              <div className="mt-5 p-4 rounded-xl bg-gradient-to-br from-[#6366F1]/12 to-[#D946EF]/8 border border-[#6366F1]/30 max-w-3xl" data-testid="guru-story">
                <div className="text-[10px] uppercase tracking-widest text-indigo-300 mb-2">The Awakening Story</div>
                <p className="text-sm leading-relaxed text-foreground/90">{c.guru_story}</p>
              </div>
            )}
            {c.royal_story && (
              <div className="mt-5 p-4 rounded-xl bg-gradient-to-br from-[#7C2D12]/12 to-[#FCD34D]/10 border border-[#FCD34D]/30 max-w-3xl" data-testid="royal-story">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2">The Path to the Throne</div>
                <p className="text-sm leading-relaxed text-foreground/90">{c.royal_story}</p>
              </div>
            )}
            {c.internet_fame_story && (
              <div className="mt-5 p-4 rounded-xl bg-gradient-to-br from-[#EC4899]/12 to-[#FACC15]/10 border border-[#EC4899]/30 max-w-3xl" data-testid="internet-fame-story">
                <div className="text-[10px] uppercase tracking-widest text-pink-300 mb-2">How They Went Viral</div>
                <p className="text-sm leading-relaxed text-foreground/90">{c.internet_fame_story}</p>
              </div>
            )}
          </div>
        </section>

        {/* D1 chart — ALWAYS FREE (the public preview) */}
        <section className="mb-10" data-testid="celebrity-d1-chart">
          <div className="card-surface rounded-xl p-5 max-w-md mx-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif-display text-xl">D1 · Rasi Chart</h2>
              <span className="text-xs label-eyebrow text-gold">Lagna: {data.kundali.lagna.rashi}</span>
            </div>
            <div className="flex justify-center">
              <KundaliChart kundali={data.kundali} size={320} />
            </div>
            <p className="text-xs text-muted mt-3 normal-case tracking-normal text-center">
              The body, personality and visible life events.
            </p>
          </div>
          {!isPreview && (
            <p className="text-xs text-center text-muted mt-4 normal-case tracking-normal">
              D9 Navamsa + Dasha analysis below · <span className="text-gold">unlock all 1,500+ charts for ₹15,000 lifetime</span>
            </p>
          )}
        </section>

        {/* PREMIUM — D9 chart + Dasha verdict + per-lord breakdown +
            Key life events + Vimshottari Lifetime Timeline.
            Free for the 3 preview celebrities; paywalled for everyone else.
            NOTE: Everything below the D1 chart is premium — D1 (Rasi) is
            the ONLY chart shown to unauthenticated / unpaid users. */}
        {isPreview ? (
          <PremiumCelebritySection
            data={data}
            slug={slug}
            mdColor={mdColor}
            adColor={adColor}
            renderInline={renderInline}
          />
        ) : (
          <PaywallGate
            tool="celebrity_data"
            toolLabel="AstroKuber Celebrity Data · Lifetime Access"
            birthDetails={CELEB_LIFETIME_BIRTH_DETAILS}
          >
            <PremiumCelebritySection
              data={data}
              slug={slug}
              mdColor={mdColor}
              adColor={adColor}
              renderInline={renderInline}
            />
          </PaywallGate>
        )}

        {/* CTA to compute user's own chart */}
        <div className="mt-8 text-center">
          <Link
            to="/dasha"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#FFB800] to-[#F59E0B] text-[#1A1410] font-medium hover:scale-[1.02] transition-all shadow-lg"
            data-testid="compute-own-dasha-cta"
          >
            <Sparkle size={14} weight="fill" /> Run the same analysis on your own chart →
          </Link>
        </div>
      </div>
      <CelebrityAskAIWidget slug={slug} celebrityName={c.name} />
    </PageShell>
  );
}

// ── PREMIUM PANEL ──────────────────────────────────────────────────────────
// Everything inside this component is FREE for the 3 preview celebrities
// (Steve Jobs / Eminem / Edwin Castro) and PAYWALLED for the other 48.
function PremiumCelebritySection({ data, slug, mdColor, adColor, renderInline }) {
  const rd = data.rise_dasha;
  return (
    <>
      {/* D9 Navamsa + D11 Rudramsha — side by side on desktop, stacked on mobile */}
      <section className="mb-10 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-testid="celebrity-divisional-charts">
        <div className="card-surface rounded-xl p-5" data-testid="celebrity-d9-chart">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif-display text-xl">D9 · Navamsa</h2>
            <span className="text-xs label-eyebrow text-gold">Lagna: {data.navamsa.lagna.rashi}</span>
          </div>
          <div className="flex justify-center">
            <KundaliChart kundali={data.navamsa} size={300} />
          </div>
          <p className="text-xs text-muted mt-3 normal-case tracking-normal text-center">
            Dharma layer — wealth permanence, marital karma, second half of life.
          </p>
        </div>
        {data.d11 && (
          <div className="card-surface rounded-xl p-5" data-testid="celebrity-d11-chart">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif-display text-xl">D11 · Rudramsha</h2>
              <span className="text-xs label-eyebrow text-gold">Lagna: {data.d11.lagna.rashi}</span>
            </div>
            <div className="flex justify-center">
              <KundaliChart kundali={data.d11} size={300} />
            </div>
            <p className="text-xs text-muted mt-3 normal-case tracking-normal text-center">
              11th-house refinement — gains, large income, fulfilled desires, networks.
            </p>
          </div>
        )}
      </section>

      {/* Varshaphal · Tajaka annual chart — year-selectable */}
      {data.rise_varshaphal && data.rise_varshaphal.chart && (
        <VarshaphalPanel slug={slug} initial={data.rise_varshaphal} riseEventLabel={data.rise_event?.label} />
      )}

      {/* Nakshatras + Padas table for Lagna + 9 planets */}
      {Array.isArray(data.nakshatras_padas) && data.nakshatras_padas.length > 0 && (
        <section className="mb-10 card-surface rounded-xl p-5 sm:p-6 max-w-4xl mx-auto" data-testid="celebrity-nakshatras-padas">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display text-xl">Nakshatras &amp; Padas</h2>
            <span className="text-xs label-eyebrow text-gold">Lagna + 9 planets</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-soft text-[10px] uppercase tracking-widest text-muted">
                  <th className="text-left py-2 pr-3">Body</th>
                  <th className="text-left py-2 pr-3">Sign</th>
                  <th className="text-left py-2 pr-3">Deg</th>
                  <th className="text-left py-2 pr-3">Nakshatra</th>
                  <th className="text-center py-2 pr-3">Pada</th>
                  <th className="text-left py-2">Nak. Lord</th>
                </tr>
              </thead>
              <tbody>
                {data.nakshatras_padas.map((r, i) => {
                  const color = PLANET_COLOR[r.body] || "#FFB800";
                  return (
                    <tr key={r.body + i} className="border-b border-soft/40 last:border-0 hover:bg-bg/30 transition-colors">
                      <td className="py-2.5 pr-3 font-medium" style={{ color }}>{r.body}</td>
                      <td className="py-2.5 pr-3 text-foreground/90">{r.rashi}</td>
                      <td className="py-2.5 pr-3 text-muted tabular-nums">{r.degrees_in_rashi.toFixed(2)}°</td>
                      <td className="py-2.5 pr-3 text-foreground/90">{r.nakshatra}</td>
                      <td className="py-2.5 pr-3 text-center">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#FFB800]/15 text-gold text-xs font-semibold tabular-nums">
                          {r.pada}
                        </span>
                      </td>
                      <td className="py-2.5 text-muted">{r.nakshatra_lord}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted mt-3 normal-case tracking-normal">
            Each nakshatra is 13°20&apos; wide, split into 4 padas of 3°20&apos; each. The pada
            colours how the nakshatra expresses itself for that planet.
          </p>
        </section>
      )}

      {/* Rise event + Dasha verdict */}
      <section className="card-surface rounded-2xl p-6 sm:p-8 relative overflow-hidden" data-testid="rise-dasha-analysis">
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: `linear-gradient(90deg, ${mdColor}, ${adColor})` }} />

        <div className="label-eyebrow text-gold mb-2 flex items-center gap-2">
          <Sparkle size={14} weight="fill" /> Rise event
        </div>
        <h2 className="font-serif-display text-3xl sm:text-4xl font-light leading-tight">
          {data.rise_event.label}
        </h2>
        <div className="text-muted text-sm mt-2">
          {new Date(data.rise_event.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </div>
        {data.rise_event.context && (
          <p className="text-muted text-base mt-3 max-w-3xl font-light leading-relaxed">{data.rise_event.context}</p>
        )}

        {/* Dasha badges — 2 levels for most, 4 levels for lottery winners */}
        <div className="flex flex-wrap gap-3 mt-6" data-testid="dasha-badges">
          <div className="inline-flex flex-col gap-1 px-4 py-3 rounded-xl border" style={{ borderColor: mdColor + "55", background: mdColor + "12" }}>
            <span className="text-[10px] uppercase tracking-widest" style={{ color: mdColor }}>Mahadasha</span>
            <span className="font-serif-display text-2xl" style={{ color: mdColor }}>{rd.md_lord}</span>
            <span className="text-[11px] text-muted normal-case tracking-normal">
              {rd.md_period.start.slice(0,10)} → {rd.md_period.end.slice(0,10)}
            </span>
          </div>
          <div className="inline-flex flex-col gap-1 px-4 py-3 rounded-xl border" style={{ borderColor: adColor + "55", background: adColor + "12" }}>
            <span className="text-[10px] uppercase tracking-widest" style={{ color: adColor }}>Antardasha</span>
            <span className="font-serif-display text-2xl" style={{ color: adColor }}>{rd.ad_lord}</span>
            <span className="text-[11px] text-muted normal-case tracking-normal">
              {rd.ad_period.start.slice(0,10)} → {rd.ad_period.end.slice(0,10)}
            </span>
          </div>
          {rd.pd_lord && (() => {
            const pdColor = PLANET_COLOR[rd.pd_lord] || "#FFB800";
            return (
              <div className="inline-flex flex-col gap-1 px-4 py-3 rounded-xl border" style={{ borderColor: pdColor + "55", background: pdColor + "12" }}>
                <span className="text-[10px] uppercase tracking-widest" style={{ color: pdColor }}>Pratyantar</span>
                <span className="font-serif-display text-2xl" style={{ color: pdColor }}>{rd.pd_lord}</span>
                <span className="text-[11px] text-muted normal-case tracking-normal">
                  {rd.pd_period.start.slice(0,10)} → {rd.pd_period.end.slice(0,10)}
                </span>
              </div>
            );
          })()}
          {rd.sd_lord && (() => {
            const sdColor = PLANET_COLOR[rd.sd_lord] || "#FFB800";
            return (
              <div className="inline-flex flex-col gap-1 px-4 py-3 rounded-xl border" style={{ borderColor: sdColor + "55", background: sdColor + "12" }}>
                <span className="text-[10px] uppercase tracking-widest" style={{ color: sdColor }}>Sookshma</span>
                <span className="font-serif-display text-2xl" style={{ color: sdColor }}>{rd.sd_lord}</span>
                <span className="text-[11px] text-muted normal-case tracking-normal">
                  {rd.sd_period.start.slice(0,10)} → {rd.sd_period.end.slice(0,10)}
                </span>
              </div>
            );
          })()}
        </div>

        {/* Combined verdict */}
        <div className="mt-7 p-5 rounded-xl bg-gradient-to-br from-[#FFB800]/8 to-transparent border border-[#FFB800]/25">
          <div className="text-xs label-eyebrow text-gold mb-3">Why this Dasha catapulted them</div>
          <p className="text-base sm:text-lg font-light leading-relaxed">{renderInline(rd.verdict)}</p>
        </div>

        {/* Per-lord breakdown */}
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div className="p-4 rounded-xl border" style={{ borderColor: mdColor + "33", background: mdColor + "08" }}>
            <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: mdColor }}>
              Mahadasha: {rd.md_lord}
            </div>
            <p className="text-sm font-light leading-relaxed">{renderInline(rd.md_why.why)}</p>
          </div>
          <div className="p-4 rounded-xl border" style={{ borderColor: adColor + "33", background: adColor + "08" }}>
            <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: adColor }}>
              Antardasha: {rd.ad_lord}
            </div>
            <p className="text-sm font-light leading-relaxed">{renderInline(rd.ad_why.why)}</p>
          </div>
          {rd.pd_why && (() => {
            const pdColor = PLANET_COLOR[rd.pd_lord] || "#FFB800";
            return (
              <div className="p-4 rounded-xl border" style={{ borderColor: pdColor + "33", background: pdColor + "08" }}>
                <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: pdColor }}>
                  Pratyantar: {rd.pd_lord}
                </div>
                <p className="text-sm font-light leading-relaxed">{renderInline(rd.pd_why.why)}</p>
              </div>
            );
          })()}
          {rd.sd_why && (() => {
            const sdColor = PLANET_COLOR[rd.sd_lord] || "#FFB800";
            return (
              <div className="p-4 rounded-xl border" style={{ borderColor: sdColor + "33", background: sdColor + "08" }}>
                <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: sdColor }}>
                  Sookshma: {rd.sd_lord}
                </div>
                <p className="text-sm font-light leading-relaxed">{renderInline(rd.sd_why.why)}</p>
              </div>
            );
          })()}
        </div>

        <p className="text-xs text-muted mt-6 normal-case tracking-normal">
          Charts and Dasha periods computed live via Swiss Ephemeris (Lahiri ayanamsa).
          The mathematical &quot;why&quot; surfaces only the verifiable mechanisms — house tenancy, lordship and karaka — no speculative if/but language.
        </p>
      </section>

      {/* ── 5 additional key life events with MD/AD/PD chains (premium) ── */}
      {Array.isArray(data.key_events) && data.key_events.length > 0 && (
        <KeyEventsTimeline events={data.key_events} riseEventDate={data.rise_event?.date} />
      )}

      {/* ── Lifetime Vimshottari Dasha Timeline (premium) ── */}
      {data.dasha_tree && Array.isArray(data.dasha_tree.mahadashas) && data.dasha_tree.mahadashas.length > 0 && (
        <DashaTimeline tree={data.dasha_tree} />
      )}
    </>
  );
}

// ── Year-selectable Varshaphal panel (top-right toggle) ───────────────────
function VarshaphalPanel({ slug, initial, riseEventLabel }) {
  const [vp, setVp] = useState(initial);
  const [year, setYear] = useState(initial.target_year);
  const [loading, setLoading] = useState(false);

  const handleYearChange = async (newYear) => {
    if (newYear === year) return;
    setYear(newYear);
    setLoading(true);
    try {
      const { data } = await api.get(`/celebrities/varshaphal/${slug}/${newYear}`);
      setVp(data.varshaphal);
    } catch {
      // fall back silently — year-selector keeps last good chart
    } finally {
      setLoading(false);
    }
  };

  const rangeText = (() => {
    if (!vp.varsha_start_iso) return null;
    const start = new Date(vp.varsha_start_iso);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    return `${fmt(start)} → ${fmt(end)}`;
  })();

  const startYear = initial.target_year - 50;
  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = Math.max(1700, startYear); y <= Math.min(2200, startYear + 110); y++) arr.push(y);
    return arr;
  }, [startYear]);

  return (
    <section className="mb-10 card-surface rounded-xl p-5 sm:p-6 max-w-4xl mx-auto bg-gradient-to-br from-[#F59E0B]/8 via-[#EC4899]/5 to-[#8B5CF6]/8 border border-[#F59E0B]/30" data-testid="celebrity-varshaphal">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <h2 className="font-serif-display text-xl">
          Varshaphal · Annual Chart {vp.target_year}
        </h2>
        <div className="flex items-center gap-2 ml-auto">
          <label htmlFor="varsha-year-select" className="text-[10px] uppercase tracking-widest text-muted">Year</label>
          <select
            id="varsha-year-select"
            value={year}
            onChange={(e) => handleYearChange(parseInt(e.target.value, 10))}
            disabled={loading}
            className="text-sm font-medium font-mono px-3 py-1.5 rounded-full border-2 border-amber-400/60 bg-amber-500/10 text-foreground/90 hover:bg-amber-500/20 hover:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="varsha-year-select"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y} className="bg-bg text-foreground">{y}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4 -mt-1 flex-wrap text-xs">
        <span className="label-eyebrow text-gold">Tajaka · Solar Return · {vp.year_of_life_label}</span>
        {rangeText && (
          <>
            <span className="text-muted">·</span>
            <span className="text-amber-200 font-mono tabular-nums" data-testid="varsha-range">
              {rangeText}
            </span>
          </>
        )}
      </div>
      <p className="text-xs text-muted mb-4 normal-case tracking-normal">
        Solar-return chart cast for this Tajaka year — Vedic annual horoscope. The
        chart covers the period above (sun-return to next sun-return).{" "}
        {riseEventLabel && year === initial.target_year && (
          <>This was also the year of <strong className="text-foreground/90">&quot;{riseEventLabel}&quot;</strong>.</>
        )}
      </p>
      <div className={`grid md:grid-cols-[1fr,300px] gap-6 items-start transition-opacity ${loading ? "opacity-50" : ""}`}>
        <div className="space-y-3 order-2 md:order-1">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="p-3 rounded-lg bg-bg/40 border border-soft">
              <div className="label-eyebrow text-amber-300 mb-1">Varsha Lagna</div>
              <div className="font-medium text-foreground/90">{vp.chart.lagna.rashi}</div>
              <div className="text-muted text-[10px] mt-0.5">ruled by {vp.chart.lagna.rashi_lord}</div>
            </div>
            <div className="p-3 rounded-lg bg-bg/40 border border-soft">
              <div className="label-eyebrow text-pink-300 mb-1">Muntha</div>
              <div className="font-medium text-foreground/90">{vp.muntha.rashi}</div>
              <div className="text-muted text-[10px] mt-0.5">in house {vp.muntha.house}</div>
            </div>
            <div className="p-3 rounded-lg bg-bg/40 border border-soft col-span-2">
              <div className="label-eyebrow text-violet-300 mb-1">Year-Lord (Varshesh)</div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <span className="font-medium text-foreground/90">{vp.year_lord.planet}</span>
                  <span className="text-muted"> · house {vp.year_lord.house}</span>
                </div>
                <div className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30">
                  {vp.year_lord.role}
                </div>
              </div>
              {Array.isArray(vp.year_lord.reasons) && vp.year_lord.reasons.length > 0 && (
                <div className="text-muted text-[11px] mt-1.5 leading-relaxed">
                  {vp.year_lord.reasons.join(" · ")}
                </div>
              )}
            </div>
          </div>
          <p className="text-sm text-foreground/85 leading-relaxed pt-1">
            {renderInline(vp.verdict)}
          </p>
        </div>
        <div className="flex justify-center order-1 md:order-2">
          <KundaliChart kundali={vp.chart} size={300} />
        </div>
      </div>
    </section>
  );
}

// ── Vimshottari Dasha Lifetime Timeline (collapsible MD → AD → PD) ─────────
const DASHA_PLANET_COLOR = {
  Sun: "#F59E0B", Moon: "#94A3B8", Mars: "#EF4444", Mercury: "#10B981",
  Jupiter: "#F59E0B", Venus: "#EC4899", Saturn: "#3B82F6", Rahu: "#8B5CF6", Ketu: "#A855F7",
};

function _fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function _fmtMonthYear(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function KeyEventsTimeline({ events, riseEventDate }) {
  // Sort by date ascending; skip an event if it duplicates the rise event
  const sorted = [...events].sort((a, b) => (a.date < b.date ? -1 : 1));
  return (
    <section className="mt-12 mb-8 max-w-4xl mx-auto" data-testid="celebrity-key-events">
      <div className="flex items-center gap-2 mb-1.5">
        <Calendar size={18} weight="duotone" className="text-gold" />
        <h2 className="font-serif-display text-xl sm:text-2xl">Key Life Events &amp; the Dasha Running at Each</h2>
      </div>
      <p className="text-xs text-muted normal-case tracking-normal mb-5 leading-relaxed">
        Five additional life moments — career milestones, personal events, controversies — each timestamped with the
        Vimshottari <strong className="text-foreground/85">Mahadasha → Antardasha → Pratyantardasha</strong> running at that moment.
        This is how astrologers reverse-engineer real lives.
      </p>
      <div className="space-y-2">
        {sorted.map((ev, i) => {
          const chain = ev.dasha_chain;
          const dotColor = chain?.md_lord ? (DASHA_PLANET_COLOR[chain.md_lord] || "#FFB800") : "#FFB800";
          const isRise = riseEventDate && ev.date === riseEventDate;
          return (
            <div
              key={`${ev.date}-${i}`}
              className={`card-surface rounded-lg p-3.5 sm:p-4 border ${isRise ? "border-emerald-500/50 bg-gradient-to-r from-emerald-500/10 to-transparent" : "border-soft"}`}
              data-testid={`key-event-${i}`}
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex flex-col items-center pt-0.5 flex-none">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: dotColor }} />
                  <span className="text-[10px] uppercase tracking-widest text-muted mt-1 leading-tight">
                    {_fmtMonthYear(ev.date)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="font-medium text-sm sm:text-base text-foreground/90 leading-snug">
                      {ev.label}
                      {isRise && (
                        <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 align-middle">
                          Main rise
                        </span>
                      )}
                    </div>
                    {chain?.label && (
                      <span
                        className="text-[11px] sm:text-xs font-mono tabular-nums px-2.5 py-1 rounded-full text-white font-semibold border leading-none whitespace-nowrap shadow-sm"
                        style={{
                          background: `linear-gradient(135deg, ${DASHA_PLANET_COLOR[chain.md_lord] || "#F59E0B"}, ${DASHA_PLANET_COLOR[chain.ad_lord] || DASHA_PLANET_COLOR[chain.md_lord] || "#F59E0B"})`,
                          borderColor: `${DASHA_PLANET_COLOR[chain.md_lord] || "#F59E0B"}80`,
                        }}
                        title="Mahadasha → Antardasha → Pratyantardasha at this date"
                      >
                        {chain.label}
                      </span>
                    )}
                  </div>
                  {ev.context && (
                    <div className="text-xs text-muted mt-1.5 leading-relaxed">{ev.context}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Vimshottari Dasha Lifetime Timeline (collapsible MD → AD → PD) ─────────

function DashaTimeline({ tree }) {
  const [openMd, setOpenMd] = useState(() => {
    // Auto-expand the current MD on first render
    const cur = (tree?.mahadashas || []).findIndex(m => m.is_current);
    return cur >= 0 ? cur : null;
  });

  return (
    <section className="mt-12 mb-10 max-w-4xl mx-auto" data-testid="celebrity-dasha-timeline">
      <div className="flex items-center gap-2 mb-2">
        <ClockCounterClockwise size={18} weight="duotone" className="text-gold" />
        <h2 className="font-serif-display text-xl sm:text-2xl">Vimshottari Dasha · Lifetime Timeline</h2>
      </div>
      <p className="text-xs text-muted normal-case tracking-normal mb-5 leading-relaxed">
        Every mahadasha from birth to {_fmtDate(tree.end_iso)} — a full 120-year Vimshottari cycle. Tap a mahadasha to open its antardashas;
        tap an antardasha to drill into its pratyantardashas.
      </p>
      <div className="space-y-1.5">
        {tree.mahadashas.map((md, i) => {
          const isOpen = openMd === i;
          const dotColor = DASHA_PLANET_COLOR[md.lord] || "#FFB800";
          return (
            <div key={`${md.lord}-${md.start}`} className="card-surface rounded-lg overflow-hidden border border-soft" data-testid={`md-row-${i}`}>
              <button
                type="button"
                onClick={() => setOpenMd(isOpen ? null : i)}
                className={`w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-bg/30 transition-colors ${md.is_current ? "bg-gradient-to-r from-[#FFB800]/15 to-transparent" : ""}`}
                data-testid={`md-toggle-${i}`}
              >
                <CaretRight size={14} weight="bold" className={`text-muted transition-transform flex-none ${isOpen ? "rotate-90" : ""}`} />
                <span className="inline-block w-2.5 h-2.5 rounded-full flex-none" style={{ background: dotColor }} />
                <span className="font-medium text-sm sm:text-base text-foreground/90 min-w-[64px]">{md.lord}</span>
                <span className="text-muted text-xs sm:text-sm tabular-nums">
                  {_fmtDate(md.start)} → {_fmtDate(md.end)}
                </span>
                <span className="ml-auto text-[10px] tabular-nums px-2 py-0.5 rounded-full bg-bg/40 border border-soft text-muted">
                  {md.years}y
                </span>
                {md.is_current && (
                  <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                    Current
                  </span>
                )}
                {md.is_past && !md.is_current && (
                  <span className="text-[9px] uppercase tracking-widest text-muted/60">Past</span>
                )}
              </button>
              {isOpen && (
                <div className="border-t border-soft bg-bg/20" data-testid={`md-body-${i}`}>
                  <DashaAntarList ads={md.antardashas} mdLord={md.lord} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function DashaAntarList({ ads, mdLord }) {
  const [openAd, setOpenAd] = useState(() => {
    const cur = ads.findIndex(a => a.is_current);
    return cur >= 0 ? cur : null;
  });
  return (
    <ul className="py-1.5">
      {ads.map((ad, j) => {
        const isOpen = openAd === j;
        const dotColor = DASHA_PLANET_COLOR[ad.lord] || "#FFB800";
        return (
          <li key={`${ad.lord}-${ad.start}`} className="px-4" data-testid={`ad-row-${j}`}>
            <button
              type="button"
              onClick={() => setOpenAd(isOpen ? null : j)}
              className={`w-full pl-6 pr-2 py-2 flex items-center gap-3 text-left rounded hover:bg-bg/40 transition-colors ${ad.is_current ? "bg-emerald-500/8" : ""}`}
              data-testid={`ad-toggle-${j}`}
            >
              <CaretRight size={12} weight="bold" className={`text-muted transition-transform flex-none ${isOpen ? "rotate-90" : ""}`} />
              <span className="inline-block w-2 h-2 rounded-full flex-none opacity-60" style={{ background: dotColor }} />
              <span className="text-xs sm:text-sm text-foreground/85">
                <span className="text-muted">{mdLord}</span>
                <span className="text-muted mx-1">→</span>
                <span className="font-medium">{ad.lord}</span>
              </span>
              <span className="ml-auto text-[11px] text-muted tabular-nums">
                {_fmtDate(ad.start)} → {_fmtDate(ad.end)}
              </span>
              {ad.is_current && (
                <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                  Current
                </span>
              )}
            </button>
            {isOpen && (
              <ul className="ml-10 pl-4 my-1.5 border-l border-soft/60 space-y-0.5" data-testid={`pd-list-${j}`}>
                {ad.pratyantardashas.map((pd, k) => {
                  const pdDot = DASHA_PLANET_COLOR[pd.lord] || "#FFB800";
                  return (
                    <li
                      key={`${pd.lord}-${pd.start}`}
                      className={`flex items-center gap-3 py-1 px-2 rounded text-[11px] sm:text-xs ${pd.is_current ? "bg-emerald-500/8 ring-1 ring-emerald-500/30" : ""}`}
                      data-testid={`pd-row-${k}`}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full flex-none opacity-60" style={{ background: pdDot }} />
                      <span className="text-foreground/75">
                        <span className="text-muted">{mdLord} {ad.lord}</span>
                        <span className="text-muted mx-1">→</span>
                        <span className="font-medium">{pd.lord}</span>
                      </span>
                      <span className="ml-auto text-muted tabular-nums">
                        {_fmtDate(pd.start)} → {_fmtDate(pd.end)}
                      </span>
                      {pd.is_current && (
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                          Now
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}
